/**
 * Ask — the front door to Deep thinking.
 *
 * Nine engines, nine plain questions. Each card asks the question a person
 * would ask, answers it from the same engine its room uses, says how sure it
 * is and why, and names what would change the answer. The working is one
 * click away in the room, Sky will explain it, and the decision can be
 * recorded where the Decision Room keeps its record.
 *
 * Nothing here decides anything new. When an engine cannot answer, the card
 * says "can't say" and why — the same honesty the rooms keep.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { useSession } from "../../app/SessionContext";
import {
  useCanWork,
  useCounterfactual,
  useCriticalPositions,
  useDriftChecks,
  useLedgerFacts,
  useLiabilityCurve,
  usePlan,
  usePopulations,
  useProgramme,
} from "../../api/queries";
import { byDesk, decidedFraction } from "../../domain/programme";
import { allInert, byEffect, movedAnything } from "../../domain/counterfactual";
import { byEnvelope, costOfWaiting, coverage } from "../../domain/liability";
import { workVerdict } from "../../domain/weather";
import { state as driftState } from "../../domain/drift";
import { shape } from "../../domain/reliability";
import { blockedBy, outcome } from "../../domain/planning";
import { appendRoomLog, type RoomRecord } from "../decision/roomStore";
import { useAssistant } from "../../assistant/AssistantContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Field, Input, Select } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { formatDateTime } from "../../lib/format";
import { locale, t, tn } from "../../i18n";
import { ROOMS } from "./Rooms";
import { reportCrash } from "../../app/crashGuard";
import r from "./Think.module.css";

type Tone = "good" | "warn" | "bad" | "unknown";

export interface Answer {
  tone: Tone;
  /** the answer in a sentence */
  headline: string;
  detail?: string;
  /** 0–5 dots, or null when it cannot be said at all */
  sure: number | null;
  sureWhy: string;
  /** what would change the answer */
  changes: string[];
  /** where the working is */
  open: string;
}

type Got = { state: "loading" } | { state: "error"; message: string } | { state: "ready"; answer: Answer };

const TONE_LABEL: Record<Tone, string> = {
  good: "Clear",
  warn: "Needs attention",
  bad: "Act on this",
  unknown: "Can't say yet",
};
const TONE_ICON: Record<Tone, string> = { good: "✓", warn: "!", bad: "✕", unknown: "?" };

const YEAR = new Date().getUTCFullYear();
const CATEGORIES = ["crack-seal", "line-marking", "sweeping"];

function money(n: number | undefined, cur = "SAR"): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  const v =
    abs >= 1e6
      ? `${(n / 1e6).toLocaleString(locale(), { maximumFractionDigits: 1 })}m`
      : abs >= 1e3
        ? `${Math.round(n / 1e3).toLocaleString(locale())}k`
        : Math.round(n).toLocaleString(locale());
  return `${cur} ${v}`;
}
/** Tonight's closing window, 22:00 to 05:00 local, from a moment. */
function tonight(now: Date): { from: string; to: string } {
  const d = new Date(now);
  d.setHours(22, 0, 0, 0);
  if (now.getTime() > d.getTime() + 7 * 3600_000) d.setDate(d.getDate() + 1);
  return { from: d.toISOString(), to: new Date(d.getTime() + 7 * 3600_000).toISOString() };
}
/** The moment the page was opened: every question is asked at the same instant. */
const useOpenedAt = () => useState(() => new Date())[0];

const dots = (f: number | null | undefined) =>
  f == null || !Number.isFinite(f) ? null : Math.max(0, Math.min(5, Math.round(f * 5)));
const pct = (f: number) => `${Math.round(f * 100)}%`;
/**
 * An engine's answer, read into a card. Reading it is guarded: an answer in a
 * shape this card did not expect makes the CARD say so, and is reported —
 * it never takes the page, or the app, down with it.
 */
function safely(key: string, read: () => Answer): Got {
  try {
    return { state: "ready", answer: read() };
  } catch (err) {
    reportCrash(`ask card "${key}"`, err);
    return {
      state: "error",
      message: t("This answer came back in a shape the card could not read. It has been reported."),
    };
  }
}
const fromQuery = <T,>(
  q: { isPending: boolean; isError: boolean; error: unknown; data?: T },
  read: (d: T) => Answer,
  key = "?",
): Got =>
  q.isError
    ? { state: "error", message: q.error instanceof Error ? q.error.message : String(q.error) }
    : q.data === undefined
      ? { state: "loading" }
      : safely(key, () => read(q.data as T));

/* ── the answers ──────────────────────────────────────────────────────── */

function useProgrammeAnswer(): Got {
  const q = useProgramme(YEAR);
  return fromQuery(
    q,
    (p) => {
      if (!p.available)
        return {
          tone: "unknown",
          headline: p.whyNot ?? t("The program cannot be built yet."),
          sure: null,
          sureWhy: t("the engine had nothing to reason about"),
          changes: [],
          open: "/decide?tab=plan",
        };
      const f = decidedFraction(p);
      const desks = byDesk(p.undecidable).slice(0, 3);
      return {
        tone: p.undecidable.length ? "warn" : "good",
        headline: t("Do {n} this year, for {cost}.", {
          n: tn(p.scheduled.length, "{n} job", "{n} jobs"),
          cost: money(p.totals.scheduledCost, p.currency),
        }),
        detail: [
          p.deferred.length
            ? t("{n} wait for money; waiting adds {esc}.", {
                n: p.deferred.length,
                esc: money(p.totals.deferredEscalation, p.currency),
              })
            : null,
          p.undecidable.length
            ? t("{n} cannot be decided until somebody supplies a missing fact.", { n: p.undecidable.length })
            : null,
        ]
          .filter(Boolean)
          .join(" "),
        sure: dots(f),
        sureWhy: f == null ? t("nothing to decide") : t("{p} of the register reached a decision", { p: pct(f) }),
        changes: desks.map((d) =>
          t("{desk} supplies {n} — that unblocks {worth}", {
            desk: d.desk,
            n: tn(d.rows.length, "{n} missing fact", "{n} missing facts"),
            worth: money(d.worth, p.currency),
          }),
        ),
        open: "/decide?tab=plan",
      };
    },
    "programme",
  );
}

function useWaitingAnswer(): Got {
  const q = useLiabilityCurve(YEAR);
  return fromQuery(
    q,
    (c) => {
      const sc = byEnvelope(c.scenarios ?? []);
      const cov = coverage(c);
      const worst = sc.find((s) => costOfWaiting(s) !== null);
      if (!c.available || !worst)
        return {
          tone: "unknown",
          headline: t("What waiting costs cannot be priced yet."),
          detail: c.coverageNote,
          sure: dots(cov),
          sureWhy: t("{n} of {m} candidates priced", { n: c.priced, m: c.candidates }),
          changes:
            c.notPricedDetail
              ?.slice(0, 3)
              .map((u) => (u as { title?: string }).title ?? "")
              .filter(Boolean) ?? [],
          open: "/decide?tab=waiting",
        };
      const best = sc.at(-1);
      return {
        tone: (costOfWaiting(worst) ?? 0) > 0 ? "warn" : "good",
        headline: t("Waiting a year on what the budget cannot fund adds {cost}.", {
          cost: money(costOfWaiting(worst) ?? 0, c.currency),
        }),
        detail:
          best && best !== worst
            ? t("At a budget of {hi} it falls to {low}.", {
                hi: money(best.envelope, c.currency),
                low: money(costOfWaiting(best) ?? 0, c.currency),
              })
            : undefined,
        sure: dots(cov),
        sureWhy: t("{n} of {m} candidates priced", { n: c.priced, m: c.candidates }),
        changes: [
          c.notPriced ? t("pricing the {n} jobs with no cost on file", { n: c.notPriced }) : null,
          worst.escalationNotComputed
            ? t("an escalation rate for {n} deferred jobs", { n: worst.escalationNotComputed })
            : null,
        ].filter((x): x is string => !!x),
        open: "/decide?tab=waiting",
      };
    },
    "waiting",
  );
}

function useLeverAnswer(): Got {
  const q = useCounterfactual(YEAR);
  return fromQuery(
    q,
    (s) => {
      if (!s.available)
        return {
          tone: "unknown",
          headline: s.whyNot ?? t("There is no program to vary yet."),
          sure: null,
          sureWhy: t("nothing to compare"),
          changes: [],
          open: "/decide?tab=change",
        };
      const facts = s.information.desks
        .slice(0, 3)
        .map((d) =>
          t("{desk}: {n} — worth {worth}", {
            desk: d.desk,
            n: tn(d.facts, "{n} fact", "{n} facts"),
            worth: money(d.worth, s.currency),
          }),
        );
      const sure = s.information.factsOutstanding ? 3 : 5;
      if (allInert(s.variants))
        return {
          tone: "warn",
          headline: t("More money or more crews would change nothing — the missing facts are the limit."),
          detail: s.information.reading,
          sure,
          sureWhy: t("every lever was tried on the platform's own model"),
          changes: facts,
          open: "/decide?tab=change",
        };
      const top = byEffect(s.variants).find(movedAnything)!;
      return {
        tone: "good",
        headline: t("{change} moves {n}.", { change: top.change, n: tn(top.diff.moved.length, "{n} job", "{n} jobs") }),
        detail: top.reading,
        sure,
        sureWhy: t("every lever was tried on the platform's own model"),
        changes: facts,
        open: "/decide?tab=change",
      };
    },
    "levers",
  );
}

function useWeatherAnswer(category: string): Got {
  const opened = useOpenedAt();
  const q = useCanWork(category, opened.toISOString());
  return fromQuery(
    q,
    (c) => {
      const v = workVerdict(c);
      const total = c.checksMade + c.unknowns.length;
      return {
        tone: v === "allowed" ? "good" : v === "blocked" ? "bad" : "unknown",
        headline:
          v === "allowed"
            ? t("Yes — {work} can go ahead now.", { work: category })
            : v === "blocked"
              ? t("Not now — {why}", { why: c.breaches[0]?.why ?? c.verdict })
              : t("Can't say — {why}", { why: c.unknowns[0]?.why ?? c.conditions.whyNot ?? c.verdict }),
        detail:
          c.breaches.length > 1
            ? c.breaches
                .slice(1)
                .map((b) => b.why)
                .join(" · ")
            : undefined,
        sure: v === "unknown" ? 0 : dots(total ? c.checksMade / total : null),
        sureWhy: t("{n} of {m} limits could be checked against measurements", { n: c.checksMade, m: total }),
        changes: c.unknowns.map((u) => u.why),
        open: "/window?tab=weather",
      };
    },
    "weather",
  );
}

function useClosureAnswer(): Got {
  const opened = useOpenedAt();
  const win = useMemo(() => tonight(opened), [opened]);
  const q = useCriticalPositions(win.from, win.to);
  return fromQuery(
    q,
    (c) => {
      if (!c.known)
        return {
          tone: "unknown",
          headline: c.whyNot ?? t("The schedule does not cover tonight."),
          sure: 0,
          sureWhy: t("no schedule for the window"),
          changes: [t("loading tonight's schedule")],
          open: "/window?tab=closure",
        };
      const n = c.critical.length;
      return {
        tone: n ? "warn" : "good",
        headline: n
          ? t("{n} cannot close tonight without stranding aircraft: {list}.", {
              n: tn(n, "{n} stand", "{n} stands"),
              list: c.critical
                .slice(0, 6)
                .map((x) => x.position)
                .join(", "),
            })
          : t("Every stand can close tonight; the rest of the estate absorbs it."),
        detail: c.flexible.length
          ? t("{n} can close freely.", { n: tn(c.flexible.length, "{n} stand", "{n} stands") })
          : undefined,
        sure: c.scheduleCoverage ? 4 : 3,
        sureWhy: c.scheduleCoverage ?? t("from the stand plan for 22:00–05:00"),
        changes: c.note ? [c.note] : [],
        open: "/window?tab=closure",
      };
    },
    "closure",
  );
}

function useWindowAnswer(position: string, category: string, hours: number): Got {
  const { site } = useSession();
  const opened = useOpenedAt();
  const query = useMemo(
    () =>
      position.trim()
        ? {
            siteId: site.id,
            positionCode: position.trim(),
            category,
            hoursNeeded: hours,
            searchFrom: opened.toISOString(),
            searchTo: new Date(opened.getTime() + 5 * 86_400_000).toISOString(),
          }
        : null,
    [site.id, position, category, hours, opened],
  );
  const q = usePlan(query);
  if (!query)
    return {
      state: "ready",
      answer: {
        tone: "unknown",
        headline: t("Name a stand or position to search."),
        sure: null,
        sureWhy: "",
        changes: [],
        open: "/window?tab=when",
      },
    };
  return fromQuery(
    q,
    (a) => {
      const o = outcome(a);
      const blocked = blockedBy(a);
      return {
        tone: o === "found" ? "good" : o === "no-window" ? "bad" : "unknown",
        headline:
          o === "found" && a.earliest
            ? t("Earliest: {from} to {to}.", {
                from: formatDateTime(a.earliest.from),
                to: formatDateTime(a.earliest.to),
              })
            : o === "no-window"
              ? t("No window in the next five days — blocked by {what}.", {
                  what: blocked.map((b) => t(b)).join(t(" and ")) || t("traffic and weather not overlapping"),
                })
              : t("Can't say — {why}", { why: a.whyNot ?? a.traffic.whyNot ?? a.weather.whyNot ?? "" }),
        detail:
          o === "found"
            ? tn(
                a.windowCount ?? a.windows.length,
                "{n} window in the next five days.",
                "{n} windows in the next five days.",
              )
            : a.caveat,
        sure: o === "unknown" ? 0 : a.traffic.known && a.weather.known ? 4 : 2,
        sureWhy: t("traffic and weather each answered separately, then joined"),
        changes: [a.traffic.whyNot, a.weather.whyNot, a.caveat].filter((x): x is string => !!x),
        open: "/window?tab=when",
      };
    },
    "window",
  );
}

function useForecastAnswer(): Got {
  const q = useDriftChecks();
  return fromQuery(
    q,
    (checks) => {
      if (!checks.length)
        return {
          tone: "unknown",
          headline: t("No forecast has been checked yet."),
          sure: null,
          sureWhy: t("no checks on record"),
          changes: [],
          open: "/trust?tab=models",
        };
      const drifted = checks.filter((c) => driftState(c) === "drifted");
      const unsure = checks.filter((c) => driftState(c) === "inconclusive");
      const judge = checks.map((c) => c.judgeableFraction ?? 0);
      const avg = judge.reduce((a, b) => a + b, 0) / judge.length;
      return {
        tone: drifted.length ? "bad" : unsure.length ? "warn" : "good",
        headline: drifted.length
          ? `${tn(drifted.length, "{n} forecast no longer describes the estate:", "{n} forecasts no longer describe the estate:")} ${drifted.map((c) => c.modelKey).join(", ")}.`
          : unsure.length
            ? t("Holding so far, but {n} cannot be judged yet.", {
                n: tn(unsure.length, "{n} forecast", "{n} forecasts"),
              })
            : t("Every forecast still describes the estate."),
        detail: t("{n} checks on record.", { n: checks.length }),
        sure: dots(avg),
        sureWhy: t("on average {p} of their record can be judged yet", { p: pct(avg) }),
        changes: unsure
          .slice(0, 3)
          .map((c) =>
            t("{model}: {n} predictions still waiting for their outcome", { model: c.modelKey, n: c.pending }),
          ),
        open: "/trust?tab=models",
      };
    },
    "forecasts",
  );
}

function useRecordsAnswer(): Got {
  const q = useLedgerFacts();
  return fromQuery(
    q,
    (facts) => {
      const shared = facts.filter((f) => f.parties > 1);
      const settled = shared.filter((f) => !!f.authority);
      if (!shared.length)
        return {
          tone: "unknown",
          headline: t("No fact is recorded by more than one party yet."),
          sure: null,
          sureWhy: t("nothing to compare"),
          changes: [],
          open: "/trust?tab=records",
        };
      return {
        tone: settled.length < shared.length ? "warn" : "good",
        headline: `${tn(shared.length, "{n} fact is recorded by more than one party;", "{n} facts are recorded by more than one party;")} ${tn(settled.length, "{n} has somebody who settles it.", "{n} have somebody who settles them.")}`,
        detail: t("Open one to see whether a difference is a process defect or noise."),
        sure: dots(shared.length ? settled.length / shared.length : null),
        sureWhy: t("share of shared facts with a named authority"),
        changes: shared
          .filter((f) => !f.authority)
          .slice(0, 3)
          .map((f) => t("{fact} has no authority to settle it", { fact: f.factKey })),
        open: "/trust?tab=records",
      };
    },
    "records",
  );
}

function useFailureAnswer(): Got {
  const { source, site } = useSession();
  const pops = usePopulations();
  const fittable = (pops.data ?? []).filter((p) => p.fittable).map((p) => p.population);
  const fits = useQueries({
    queries: fittable.map((p) => ({
      queryKey: ["reliability", "fits", site.id, p],
      queryFn: () => source.fits(site.id, p),
      staleTime: 300_000,
    })),
  });
  if (pops.isError) return { state: "error", message: String(pops.error) };
  if (!pops.data || fits.some((f) => f.isPending)) return { state: "loading" };
  return safely("failures", () => {
    const all = fits.flatMap((f) => f.data?.slice(0, 1) ?? []);
    const wear = all.filter((f) => shape(f) === "wear-out").map((f) => f.population);
    const flat = all.filter((f) => shape(f) === "constant").map((f) => f.population);
    const early = all.filter((f) => shape(f) === "infant-mortality").map((f) => f.population);
    const unsettled = all.filter((f) => shape(f) === "uncertain").length;
    const total = pops.data.length;
    if (!all.length)
      return {
        tone: "unknown",
        headline: t("No population has enough failures on record to fit yet."),
        sure: null,
        sureWhy: t("{n} populations, none fittable", { n: total }),
        changes: pops.data
          .filter((p) => !p.fittable)
          .slice(0, 3)
          .map((p) => `${p.population}: ${p.whyNot ?? ""}`),
        open: "/trust?tab=failures",
      };
    return {
      tone: wear.length ? "warn" : "good",
      headline: wear.length
        ? t("{list} wear out with age — replace them on age.", { list: wear.join(", ") })
        : t("Nothing fitted shows wear-out — replacing on age would waste money."),
      detail: [
        flat.length
          ? t("{list} fail at a constant rate: inspect, don't replace on age.", { list: flat.join(", ") })
          : null,
        early.length ? t("{list} fail early or not at all: check installation.", { list: early.join(", ") }) : null,
      ]
        .filter(Boolean)
        .join(" "),
      sure: dots(all.length ? (all.length - unsettled) / all.length : null),
      sureWhy: t("{n} of {m} fits have a shape the data can support", { n: all.length - unsettled, m: all.length }),
      changes: pops.data
        .filter((p) => !p.fittable)
        .slice(0, 3)
        .map((p) => `${p.population}: ${p.whyNot ?? t("not enough failures yet")}`),
      open: "/trust?tab=failures",
    };
  });
}

/* ── the page ─────────────────────────────────────────────────────────── */

interface Question {
  key: string;
  room: keyof typeof ROOMS;
  ask: string;
  got: Got;
  /** inputs, for the questions that need them */
  inputs?: ReactNode;
}

export function AskPage() {
  const [category, setCategory] = useState("crack-seal");
  const [position, setPosition] = useState("104");
  const [hours, setHours] = useState(4);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const questions: Question[] = [
    { key: "programme", room: "decide", ask: "What should we do this year?", got: useProgrammeAnswer() },
    { key: "waiting", room: "decide", ask: "What does waiting cost us?", got: useWaitingAnswer() },
    { key: "levers", room: "decide", ask: "Would more money or more crews help?", got: useLeverAnswer() },
    {
      key: "weather",
      room: "window",
      ask: "Can the crews work outside right now?",
      got: useWeatherAnswer(category),
      inputs: <CategoryPick value={category} onChange={setCategory} />,
    },
    { key: "closure", room: "window", ask: "Which stands can't we close tonight?", got: useClosureAnswer() },
    {
      key: "window",
      room: "window",
      ask: "When can we do the job?",
      got: useWindowAnswer(position, category, hours),
      inputs: (
        <div className={r.inputs}>
          <Field label="Stand or position">
            <Input value={position} onChange={(e) => setPosition(e.target.value)} />
          </Field>
          <CategoryPick value={category} onChange={setCategory} />
          <Field label="Hours needed">
            <Select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
              {[2, 3, 4, 6, 8].map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      ),
    },
    { key: "forecasts", room: "trust", ask: "Can we trust the forecasts?", got: useForecastAnswer() },
    { key: "records", room: "trust", ask: "Do our records agree?", got: useRecordsAnswer() },
    { key: "failures", room: "trust", ask: "Which assets wear out with age?", got: useFailureAnswer() },
  ];

  const ready = questions.filter((q) => q.got.state === "ready") as (Question & {
    got: { state: "ready"; answer: Answer };
  })[];
  const needs = ready.filter((q) => q.got.answer.tone === "bad" || q.got.answer.tone === "warn");

  useScreenContext({
    route: "/think",
    title: "Ask",
    summary: ready.slice(0, 4).map((q) => `${t(q.ask)} ${q.got.answer.headline}`),
    figures: new Set(Array.from({ length: 13 }, (_, i) => String(i))),
    offers: [{ label: "What needs my attention?", ask: "what needs my attention in deep thinking?" }],
    own: (text) =>
      /attention|need|urgent|first/.test(text.toLowerCase())
        ? needs.length
          ? needs.map((q) => `${t(q.ask)} — ${q.got.answer.headline}`).join("\n")
          : t("Nothing needs attention: every answer is clear or still being worked out.")
        : null,
    worksInFull: true,
  });

  return (
    <Page eyebrow="Deep thinking" title="Ask">
      <p className={r.lede}>
        {t(
          "Pick a question. The answer comes from the same engine its room uses, says how sure it is and what would change it — and the working is one click away.",
        )}
      </p>

      {needs.length > 0 && (
        <div className={r.attention} data-testid="ask-attention">
          <b>{tn(needs.length, "{n} answer needs your attention", "{n} answers need your attention")}</b>
          {needs.map((q) => (
            <button key={q.key} type="button" onClick={() => setOpenKey(q.key)}>
              {t(q.ask)}
            </button>
          ))}
        </div>
      )}

      {(Object.keys(ROOMS) as (keyof typeof ROOMS)[]).map((room) => (
        <section key={room} className={r.group}>
          <div className={r.groupHead}>
            <h2>{t(ROOMS[room].title)}</h2>
            <Link to={ROOMS[room].route}>{t("Open the room")} →</Link>
          </div>
          <div className={r.cards}>
            {questions
              .filter((q) => q.room === room)
              .map((q) => (
                <QuestionCard
                  key={q.key}
                  q={q}
                  open={openKey === q.key}
                  onToggle={() => setOpenKey((k) => (k === q.key ? null : q.key))}
                />
              ))}
          </div>
        </section>
      ))}
    </Page>
  );
}

function CategoryPick({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label="The work">
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        {CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {t(c)}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function Confidence({ sure, why }: { sure: number | null; why: string }) {
  const label =
    sure == null || sure === 0
      ? t("Can't say")
      : sure >= 4
        ? t("Sure")
        : sure >= 3
          ? t("Fairly sure")
          : t("Not very sure");
  return (
    <div className={r.sure} aria-label={`${label}: ${why}`}>
      <span className={r.dots} aria-hidden>
        {Array.from({ length: 5 }, (_, i) => (
          <i key={i} data-on={sure != null && i < sure} />
        ))}
      </span>
      <b>{label}</b>
      {why && <small>{why}</small>}
    </div>
  );
}

function QuestionCard({ q, open, onToggle }: { q: Question; open: boolean; onToggle: () => void }) {
  const got = q.got;
  const a = got.state === "ready" ? got.answer : null;
  return (
    <article className={r.card} data-open={open} data-tone={a?.tone ?? "loading"} data-testid={`ask-${q.key}`}>
      <button type="button" className={r.cardHead} onClick={onToggle} aria-expanded={open}>
        <span className={r.q}>{t(q.ask)}</span>
        {got.state === "loading" && <span className={r.thinking}>{t("thinking…")}</span>}
        {got.state === "error" && (
          <span className={r.chip} data-tone="unknown">
            ? {t("Can't reach the engine")}
          </span>
        )}
        {a && (
          <span className={r.chip} data-tone={a.tone}>
            {TONE_ICON[a.tone]} {t(TONE_LABEL[a.tone])}
          </span>
        )}
      </button>
      {a && <p className={r.headline}>{a.headline}</p>}
      {got.state === "error" && <p className={r.headline}>{got.message}</p>}
      {open && (
        <div className={r.body}>
          {q.inputs}
          {a?.detail && <p className={r.detail}>{a.detail}</p>}
          {a && <Confidence sure={a.sure} why={a.sureWhy} />}
          {a && a.changes.length > 0 && (
            <div className={r.changes}>
              <h4>{t("What would change this answer")}</h4>
              <ul>
                {a.changes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          )}
          {a && <Actions q={q} a={a} />}
        </div>
      )}
      {!open && (
        <button type="button" className={r.more} onClick={onToggle}>
          {q.inputs ? t("Change the question, and why") : t("Why, and what would change it")} ↓
        </button>
      )}
    </article>
  );
}

function Actions({ q, a }: { q: Question; a: Answer }) {
  const { source, site, principal } = useSession();
  const sky = useAssistant();
  const [recording, setRecording] = useState(false);
  const [choice, setChoice] = useState<"accept" | "defer" | "more">("accept");
  const [why, setWhy] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  const CHOICES = { accept: "Go with it", defer: "Not now", more: "Get the missing facts first" } as const;

  async function record() {
    const at = new Date();
    const rec: RoomRecord = {
      id: `think-${q.key}-${at.getTime().toString(36)}`,
      at: at.toISOString(),
      by: principal.name,
      decisionId: `think:${q.key}`,
      title: t(q.ask),
      option: choice,
      optionName: t(CHOICES[choice]),
      rationale: why.trim(),
      boardLine: [a.headline, a.detail ?? ""].filter(Boolean),
      evidence: [{ kind: "rule", ref: a.open, label: t("Deep thinking · {room}", { room: t(ROOMS[q.room].title) }) }],
    };
    const where = await appendRoomLog(source, site.id, rec);
    setSaved(
      where === "platform"
        ? t("Recorded on the platform — it is in the Decision Room's record.")
        : t("Recorded in this browser — the platform did not take it."),
    );
    setRecording(false);
  }

  return (
    <div className={r.actions}>
      <Link to={a.open} className={r.working}>
        {t("Show me the working")} →
      </Link>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => sky.summon(`${t(q.ask)} ${t("Explain the answer:")} ${a.headline}`)}
      >
        ✦ {t("Ask Sky why")}
      </Button>
      {!recording && !saved && (
        <Button size="sm" variant="primary" onClick={() => setRecording(true)} data-testid={`ask-record-${q.key}`}>
          {t("Record the decision")}
        </Button>
      )}
      {recording && (
        <div className={r.record}>
          <div className={r.choices} role="radiogroup" aria-label={t("What did you decide?")}>
            {(Object.keys(CHOICES) as (keyof typeof CHOICES)[]).map((k) => (
              <button key={k} type="button" role="radio" aria-checked={choice === k} onClick={() => setChoice(k)}>
                {t(CHOICES[k])}
              </button>
            ))}
          </div>
          <Input placeholder={t("Why, in a line (optional)")} value={why} onChange={(e) => setWhy(e.target.value)} />
          <div className={r.recordActions}>
            <Button size="sm" variant="ghost" onClick={() => setRecording(false)}>
              {t("Cancel")}
            </Button>
            <Button size="sm" variant="primary" onClick={() => void record()} data-testid={`ask-save-${q.key}`}>
              {t("Record it")}
            </Button>
          </div>
        </div>
      )}
      {saved && (
        <p className={r.saved} role="status">
          ✓ {saved}
        </p>
      )}
    </div>
  );
}
